import type * as highcharts from "highcharts";
import React from "react";
import { useQuery } from "react-query";
import { LoadingDots } from "web-ui";

import { getCrashes } from "api/dashboardBackend";
import type { CrashEntry, CrashesData } from "api/dashboardBackend";
import DuoErrorScreen, { DuoErrorType } from "components/DuoErrorScreen";
import SortableTable, { type Column } from "components/SortableTable";
import styles from "components/crashes/CrashesContent.module.scss";
import Chart, { ChartType } from "components/release/metric-chart/Chart";
import useErrorScreen from "components/useErrorScreen";
import { type PlatformId } from "util/platform";
import { semVerString } from "util/version";

const ERROR_TYPE_NAME = "Error Type";

const getOptions = (crashes: CrashEntry[]) => ({
  chart: { type: ChartType.PIE },
  credits: { enabled: false },
  legend: {
    align: "right",
    layout: "vertical",
    verticalAlign: "top",
  },
  plotOptions: {
    pie: {
      allowPointSelect: true,
      cursor: "pointer",
      dataLabels: {
        enabled: true,
        filter: {
          operator: ">",
          property: "percentage",
          value: 2,
        },
        formatter(this: highcharts.TooltipFormatterContextObject) {
          const value = `${this.y} (${this.percentage.toFixed(0)}%)`;
          return this.series.name === ERROR_TYPE_NAME
            ? `${this.point.name}: ${value}`
            : value;
        },
        style: {
          fontSize: "14px",
        },
      },
      states: {
        hover: {
          brightness: 0.1,
          enabled: true,
        },
      },
    },
  },
  series: [
    {
      center: ["50%", "50%"],
      data: crashes.reduce<{ name: string; y: number }[]>((acc, d) => {
        const existingError = acc.find(item => item.name === d.error);
        if (existingError) {
          existingError.y += d.occurrences;
        } else {
          acc.push({
            name: d.error,
            y: d.occurrences,
          });
        }
        return acc;
      }, []),
      name: ERROR_TYPE_NAME,
      size: "100%",
      type: ChartType.PIE,
    },
  ],
  title: { text: "Failure Type Breakdown" },
  tooltip: {
    pointFormat: "{point.name}: <b>{point.y}</b> ({point.percentage:.1f}%)",
  },
});

interface Props {
  platformId: PlatformId;
  version: string;
}

const titleColumn: Column<CrashEntry> = {
  getCell: d => d.title,
  getValue: d => d.title,
  header: "Title",
};

const subtitleColumn: Column<CrashEntry> = {
  getCell: d => d.subtitle,
  getValue: d => d.subtitle,
  header: "Subtitle",
};

const idColumn: Column<CrashEntry> = {
  getCell: d => (
    <a href={d.url} rel="noreferrer" target="_blank">
      {d.crashlytics_id}
    </a>
  ),
  getValue: d => d.crashlytics_id,
  header: "ID",
};

const errorColumn: Column<CrashEntry> = {
  getCell: d => d.error,
  getValue: d => d.error,
  header: "Error",
};

const occurrencesColumn: Column<CrashEntry> = {
  getCell: d => d.occurrences,
  getValue: d => d.occurrences,
  header: "Count",
};

const deviceColumn: Column<CrashEntry> = {
  getCell: d => d.devices,
  getValue: d => d.devices,
  header: "Device",
};

const firstSeenColumn: Column<CrashEntry> = {
  getCell: d => semVerString(d.first_seen),
  getValue: d => semVerString(d.first_seen),
  header: "First Seen",
};

const sourceColumn: Column<CrashEntry> = {
  getCell: d => (d.source === "firebase" ? "Firebase" : "Sentry"),
  getValue: d => d.source,
  header: "Source",
};

const CrashesContent = ({ platformId, version }: Props) => {
  const [includeNonExceptionFailures, setIncludeNonExceptionFailures] =
    React.useState(true);
  const [showFirebaseCrashes, setShowFirebaseCrashes] = React.useState(true);
  const [showSentryCrashes, setShowSentryCrashes] = React.useState(true);

  const { isError, error, isLoading, data } = useQuery<CrashesData>({
    queryFn: () => getCrashes(platformId, version),
    queryKey: [platformId, version],
  });
  const errorComponent = useErrorScreen(isError, error);

  if (errorComponent) {
    return errorComponent;
  }

  const columns = [
    occurrencesColumn,
    deviceColumn,
    firstSeenColumn,
    sourceColumn,
    titleColumn,
    subtitleColumn,
    idColumn,
    errorColumn,
  ];

  const filteredData = data?.data
    ? data.data.filter(crash => {
        // Filter by exception failures
        const passesExceptionFilter =
          includeNonExceptionFailures ||
          (crash.error && crash.error.trim() !== "");

        // Filter by source
        const passesSourceFilter =
          (crash.source === "firebase" && showFirebaseCrashes) ||
          (crash.source === "sentry" && showSentryCrashes);

        return passesExceptionFilter && passesSourceFilter;
      })
    : [];

  return data && data.data.length > 0 ? (
    <div className={styles.container}>
      <div className={styles.checkboxContainer}>
        <label className={styles.filterCheckbox}>
          <input
            checked={includeNonExceptionFailures}
            onChange={e => setIncludeNonExceptionFailures(e.target.checked)}
            type="checkbox"
          />
          Include non-exception failures
        </label>
        <label className={styles.filterCheckbox}>
          <input
            checked={showFirebaseCrashes}
            onChange={e => setShowFirebaseCrashes(e.target.checked)}
            type="checkbox"
          />
          Show Firebase crashes
        </label>
        <label className={styles.filterCheckbox}>
          <input
            checked={showSentryCrashes}
            onChange={e => setShowSentryCrashes(e.target.checked)}
            type="checkbox"
          />
          Show Sentry errors
        </label>
      </div>
      <div className={styles.chartContainer} id="failureTypeBreakdown">
        <Chart
          options={getOptions(filteredData) as highcharts.Options}
          uniqueChartId="failureTypeBreakdown"
        />
      </div>
      <div className={styles.crashContent}>
        <SortableTable columns={columns} data={filteredData} />
      </div>
    </div>
  ) : isLoading ? (
    <LoadingDots type="screen-white" />
  ) : data ? (
    <div className={styles.container}>
      <div className={styles.noCrashesResult}>No crashes found</div>
    </div>
  ) : (
    <DuoErrorScreen
      error={Error("Fail to load crashes")}
      errorType={DuoErrorType.UnknownError}
    />
  );
};

export default CrashesContent;
